import { jsx as _jsx } from "react/jsx-runtime";
/**
 * LanguageSelectorContainer 组件
 * 集成语言选择器与语言状态管理系统的容器组件 - 纯受控模式
 *
 * @example
 * ```tsx
 * // 受控模式 - 通过props控制状态
 * <LanguageSelectorContainer
 *   type="source"
 *   value="en"
 *   onChange={handleLanguageChange}
 * />
 *
 * // 文档特定语言选择器，仍通过props控制状态
 * <LanguageSelectorContainer
 *   type="source"
 *   documentId="doc123"
 *   value="en"
 *   onChange={handleLanguageChange}
 * />
 *
 * // 与模型关联的语言选择器，受控模式
 * <LanguageSelectorContainer
 *   type="source"
 *   modelId="deepseek-v3"
 *   value="en"
 *   onChange={handleLanguageChange}
 * />
 * ```
 */
import { useEffect, useRef } from 'react';
import { useLanguageState } from '@readsavor/core';
import { LanguageSelector } from '../../core/LanguageSelector/index.js';
/**
 * 语言选择器容器组件
 * 连接LanguageSelector与语言状态管理系统 - 纯受控模式
 */
export const LanguageSelectorContainer = ({ type, identity, modelId, excludeAuto = false, label, disabled = false, className = '', value, onChange, }) => {
    // 使用全局语言状态，但仅用于获取语言列表和加载文档设置
    const languageState = useLanguageState(modelId);
    // 用于跟踪上一次处理的文档ID
    const prevIdentityRef = useRef(undefined);
    // 用于防止重复处理
    const isProcessingRef = useRef(false);
    // 用于存储setTimeout的ID
    const timerRef = useRef(null);
    // 当documentId变化时，加载文档语言设置 - 优化依赖数组和处理逻辑
    useEffect(() => {
        // 如果没有文档ID，则不处理
        if (!identity || !identity.id) {
            return;
        }
        // 如果文档ID与上次相同，则不重复处理
        if (prevIdentityRef.current &&
            identity.id === prevIdentityRef.current.id &&
            identity.type === prevIdentityRef.current.type) {
            return;
        }
        // 如果已经在处理中，则不重复触发
        if (isProcessingRef.current) {
            return;
        }
        // 标记为处理中
        isProcessingRef.current = true;
        // 记录当前处理的文档ID
        prevIdentityRef.current = identity;
        // 清除之前的定时器（如果存在）
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
        }
        // 设置延迟执行，使用微任务打断同步更新循环
        timerRef.current = window.setTimeout(() => {
            // 再次检查文档ID是否仍然相同（以防在延迟期间发生变化）
            if (prevIdentityRef.current &&
                identity.id === prevIdentityRef.current.id &&
                identity.type === prevIdentityRef.current.type) {
                languageState.setCurrentDocument(identity);
            }
            // 处理完成，重置状态
            isProcessingRef.current = false;
            timerRef.current = null;
        }, 0);
        // 清理函数
        return () => {
            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            isProcessingRef.current = false;
        };
    }, [identity, languageState]);
    // 获取语言列表
    const languages = type === 'source' ? languageState.sourceLanguages : languageState.targetLanguages;
    // 自动生成标签文本（如果未提供）
    const autoLabel = type === 'source' ? 'Source Language' : 'Target Language';
    const effectiveLabel = label || autoLabel;
    // 处理排除自动检测的逻辑
    const shouldExcludeAuto = type === 'target' || excludeAuto;
    // 处理语言变更 - 纯受控模式，直接调用onChange
    const handleLanguageChange = (languageCode) => {
        onChange(languageCode);
    };
    return (_jsx("div", { className: `language-selector-container ${className}`, children: _jsx(LanguageSelector, { value: value, onChange: handleLanguageChange, languages: languages, excludeAuto: shouldExcludeAuto, label: effectiveLabel, disabled: disabled }) }));
};
export default LanguageSelectorContainer;
//# sourceMappingURL=LanguageSelectorContainer.js.map